const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function resolveImage(imageUrl) {
  if (!imageUrl) return "";
  return imageUrl.startsWith("http") ? imageUrl : `${API_BASE_URL}/${imageUrl.replace(/^\//, "")}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseLocalDate(str) {
  if (!str) return new Date(NaN);
  const [y, mo, d] = str.slice(0, 10).split("-").map(Number);
  return new Date(y, mo - 1, d);
}

function fmtDate(date) {
  return new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function getInitials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function normalizeId(v) {
  return v && typeof v === "object" ? v._id : v;
}

const DAY_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

const CARD_COLORS = [
  { bg: "#fff8e1", border: "#ffa726", tagBg: "#fff3cd", tagColor: "#856404" },
  { bg: "#e8f5e9", border: "#66bb6a", tagBg: "#d1e7dd", tagColor: "#0a3622" },
  { bg: "#e3f2fd", border: "#42a5f5", tagBg: "#cfe2ff", tagColor: "#084298" },
  { bg: "#fce4ec", border: "#ef5350", tagBg: "#f8d7da", tagColor: "#842029" },
  { bg: "#f3e5f5", border: "#ab47bc", tagBg: "#e9d8fd", tagColor: "#4a0e5c" },
];

/**
 * Builds one row per unique project from calendarData.
 * Merges all employees assigned to the same project into a single entry.
 * Computes the union date span across all allocations for the week.
 */
function buildProjectWiseRows(calendarData, projects, filterClientId, filterProjectId, filterAssignedById, weekStartIso, weekEndIso, clientImages = {}) {
  // projectId → aggregated entry
  const projectMap = new Map();

  calendarData.forEach((emp) => {
    (emp.assignments || []).forEach((assignment) => {
      const projectId = String(normalizeId(assignment.projectId) || assignment.projectName || "unknown");
      const clientId  = String(normalizeId(assignment.clientId)  || assignment.clientName  || "unknown");

      // Apply client / project / assigned-by filters
      if (filterClientId  && clientId  !== filterClientId)  return;
      if (filterProjectId && projectId !== filterProjectId) return;
      if (filterAssignedById && String(normalizeId(assignment.assignedById) || "") !== filterAssignedById) return;

      // Only include allocations that overlap the current week
      const startIso = assignment.startDate ? isoDate(parseLocalDate(assignment.startDate)) : null;
      const endIso   = assignment.endDate   ? isoDate(parseLocalDate(assignment.endDate))   : null;
      if (!startIso || !endIso) return;
      if (endIso < weekStartIso || startIso > weekEndIso) return;

      if (!projectMap.has(projectId)) {
        projectMap.set(projectId, {
          projectId,
          clientId,
          clientName:        assignment.clientName  || "Unknown Client",
          projectName:       assignment.projectName || "Unknown Project",
          employeeTaskPairs: new Map(),
          minStart:          startIso,
          maxEnd:            endIso,
          firstAssignmentId: assignment.assignmentId,
        });
      }

      const entry = projectMap.get(projectId);
      const pairKey = `${emp.employeeName || ""}|${assignment.taskDescription || ""}`;
      if (!entry.employeeTaskPairs.has(pairKey)) {
        entry.employeeTaskPairs.set(pairKey, {
          employee: emp.employeeName      || "—",
          task:     assignment.taskDescription || "—",
          notes:    assignment.notes || "—",
        });
      }
      if (startIso < entry.minStart) entry.minStart = startIso;
      if (endIso   > entry.maxEnd)   entry.maxEnd   = endIso;
    });
  });

  let idx = 0;
  return Array.from(projectMap.values()).map((entry) => {
    // Look up project-level dates from the full projects list
    const projectDoc       = projects.find((p) => String(p._id) === String(entry.projectId));
    const projectStartDate = projectDoc?.startDate || entry.minStart;
    const projectEndDate   = projectDoc?.endDate   || entry.maxEnd;

    const employeeTaskPairs = [...entry.employeeTaskPairs.values()];
    const employeeList = employeeTaskPairs.map((p) => p.employee);
    const taskDescPreview = employeeTaskPairs.length > 0
      ? employeeTaskPairs[0].task.length > 60
        ? employeeTaskPairs[0].task.slice(0, 57) + "…"
        : employeeTaskPairs[0].task
      : "—";

    // Client logo if uploaded (looked up by clientId, then clientName); else falls back to initials.
    const clientImage = clientImages[String(entry.clientId)] || clientImages[entry.clientName] || "";

    return {
      key:          `pw-${entry.projectId}-${idx}`,
      initials:     entry.clientName,
      employeeImage: clientImage,
      heading:      entry.clientName,
      headingRight: "",
      line1:        entry.projectName,
      line2:        taskDescPreview,
      line3:        "",
      employeeName: employeeList.join(", "),
      assignment: {
        assignmentId:      entry.firstAssignmentId,
        projectId:         entry.projectId,
        clientId:          entry.clientId,
        projectName:       entry.projectName,
        clientName:        entry.clientName,
        employeeTaskPairs,
        startDate:         entry.minStart,
        endDate:           entry.maxEnd,
        projectStartDate,
        projectEndDate,
      },
      color: CARD_COLORS[idx++ % CARD_COLORS.length],
    };
  });
}

/* ── Style constants (unchanged) ────────────────────────────────── */

const TH_CORNER = {
  background:   "#f8fafc",
  borderBottom: "2px solid #e2e8f0",
  borderRight:  "1px solid #e2e8f0",
  padding:      "10px 8px",
  position:     "sticky",
  left:         0,
  zIndex:       3,
  width:        70,
};

const TH_DAY = {
  background:   "#f8fafc",
  borderBottom: "2px solid #e2e8f0",
  borderRight:  "1px solid #e2e8f0",
  padding:      "10px 16px",
  textAlign:    "left",
};

const TD_LABEL = {
  fontSize:      11,
  fontWeight:    700,
  color:         "#6c757d",
  letterSpacing: "0.06em",
  textAlign:     "center",
  padding:       "8px 4px",
  background:    "#f8f9fa",
  borderRight:   "1px solid #dee2e6",
  verticalAlign: "middle",
  position:      "sticky",
  left:          0,
  zIndex:        1,
};

const TD_CELL = {
  padding:       6,
  verticalAlign: "top",
  borderRight:   "1px dashed #e9ecef",
};

/* ── Component ─────────────────────────────────────────────────── */

const HodSchedulerGrid = ({
  calendarData    = [],
  weekStart,
  filterClientId     = "",
  filterProjectId    = "",
  filterAssignedById = "",
  projects           = [],
  clientImages       = {},
  onCardClick        = () => {},
}) => {
  const weekDates    = DAY_FULL.map((_, i) => addDays(weekStart, i));
  const weekStartIso = isoDate(weekDates[0]);
  const weekEndIso   = isoDate(weekDates[4]);

  const flatRows = buildProjectWiseRows(
    calendarData,
    projects,
    filterClientId,
    filterProjectId,
    filterAssignedById,
    weekStartIso,
    weekEndIso,
    clientImages
  );

  if (!flatRows.length) {
    return (
      <div style={{
        borderRadius: 12, border: "1px solid #e2e8f0", background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "48px 20px",
        textAlign: "center", color: "#94a3b8", marginBottom: 24,
      }}>
        <i className="fa fa-calendar fa-2x mb-3 d-block" />
        <p style={{ margin: 0, fontSize: 13 }}>No assignments found for this week.</p>
      </div>
    );
  }

  return (
    <div style={{
      overflowX:    "auto",
      borderRadius: 12,
      border:       "1px solid #e2e8f0",
      background:   "#fff",
      boxShadow:    "0 2px 8px rgba(0,0,0,0.06)",
      marginBottom: 24,
    }}>
      <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed", minWidth: 640 }}>
        <colgroup>
          <col style={{ width: 70 }} />
          {DAY_FULL.map((d) => <col key={d} />)}
        </colgroup>

        <thead>
          <tr>
            <th style={TH_CORNER} />
            {DAY_FULL.map((day, i) => (
              <th key={day} style={TH_DAY}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>{day}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{fmtDate(weekDates[i])}</div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {flatRows.map(({ key, initials, employeeImage, heading, headingRight, line1, line2, line3, employeeName, assignment, color }) => {
            const startIso = isoDate(parseLocalDate(assignment.startDate));
            const endIso   = isoDate(parseLocalDate(assignment.endDate));
            if (endIso < weekStartIso || startIso > weekEndIso) return null;

            const from = Math.max(0, Math.round((parseLocalDate(startIso) - parseLocalDate(weekStartIso)) / 86400000));
            const to   = Math.min(4, Math.round((parseLocalDate(endIso)   - parseLocalDate(weekStartIso)) / 86400000));
            const span = to - from + 1;
            if (span <= 0) return null;

            const compact = !line2 && !line3;
            const cardH   = compact ? 58 : 96;

            return (
              <tr key={key} style={{ borderBottom: "1px solid #dee2e6" }}>
                {/* Avatar column */}
                <td style={{ ...TD_LABEL, width: 70 }} title={initials}>
                  {resolveImage(employeeImage) ? (
                    <img
                      src={resolveImage(employeeImage)}
                      alt={initials}
                      style={{
                        width: 34, height: 34, borderRadius: "50%",
                        objectFit: "cover", display: "block", margin: "0 auto",
                        border: "2px solid #e2e8f0",
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%",
                    background: "#6366f1", color: "#fff",
                    display: resolveImage(employeeImage) ? "none" : "flex",
                    alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, margin: "0 auto",
                  }}>
                    {getInitials(initials)}
                  </div>
                </td>

                {/* Empty cells before bar */}
                {from > 0 && <td colSpan={from} style={TD_CELL} />}

                {/* Card cell */}
                <td colSpan={span} style={{ ...TD_CELL, minHeight: cardH }}>
                  <div
                    onClick={() => onCardClick({ heading, headingRight, line1, line2, line3, employeeName, assignment, color })}
                    style={{
                      borderLeft:    `4px solid ${color.border}`,
                      background:    color.bg,
                      borderRadius:  "0 8px 8px 0",
                      padding:       compact ? "6px 10px" : "8px 10px",
                      minHeight:     cardH,
                      height:        "100%",
                      boxShadow:     "0 1px 4px rgba(0,0,0,0.07)",
                      transition:    "box-shadow 0.18s",
                      display:       "flex",
                      flexDirection: "column",
                      gap:           3,
                      boxSizing:     "border-box",
                      cursor:        "pointer",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.14)")}
                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.07)")}
                  >
                    {/* Heading — Client Name */}
                    <div style={{
                      display:        "flex",
                      justifyContent: "space-between",
                      alignItems:     "center",
                      gap:            6,
                    }}>
                      <span style={{
                        fontSize:     13,
                        fontWeight:   700,
                        color:        color.border,
                        lineHeight:   1.2,
                        overflow:     "hidden",
                        whiteSpace:   "nowrap",
                        textOverflow: "ellipsis",
                        flex:         1,
                      }}>
                        {heading}
                      </span>
                      {headingRight && (
                        <span style={{
                          fontSize:      9,
                          fontWeight:    700,
                          color:         color.tagColor,
                          background:    color.tagBg,
                          border:        `1px solid ${color.border}`,
                          padding:       "2px 6px",
                          borderRadius:  999,
                          whiteSpace:    "nowrap",
                          flexShrink:    0,
                          letterSpacing: "0.04em",
                        }}>
                          {headingRight}
                        </span>
                      )}
                    </div>

                    {/* Line 1 — Project Name */}
                    <div style={{
                      marginTop:    4,
                      fontSize:     12,
                      color:        "#334155",
                      fontWeight:   600,
                      whiteSpace:   "nowrap",
                      overflow:     "hidden",
                      textOverflow: "ellipsis",
                    }}>
                      {line1}
                    </div>

                    {/* Line 2 — Employee preview */}
                    <div style={{
                      marginTop:    2,
                      fontSize:     11,
                      color:        "#64748b",
                      whiteSpace:   "nowrap",
                      overflow:     "hidden",
                      textOverflow: "ellipsis",
                    }}>
                      {line2}
                    </div>

                    {/* Line 3 — unused, kept for structural consistency */}
                    {line3 && (
                      <div style={{
                        marginTop:    2,
                        fontSize:     11,
                        color:        "#64748b",
                        fontStyle:    "italic",
                        whiteSpace:   "nowrap",
                        overflow:     "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {line3}
                      </div>
                    )}
                  </div>
                </td>

                {/* Empty cells after bar */}
                {to < 4 && <td colSpan={4 - to} style={TD_CELL} />}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default HodSchedulerGrid;
